<template>
  <div class="quantityBox full-width" v-if="isinstock">
    <transition
      appear
      enter-active-class="animated fadeIn"
      leave-active-class="animated fadeOut"
    >
      <span
        :class="['error text-center', showMaxErrorOnTop ? 'top' : '']"
        v-if="product && false"
        v-show="cartQty >= qtyLimit && (showMaxError || focus)"
      >
        Max {{ qtyLimit }}
      </span>
    </transition>
    <template v-if="cartQty > 0 || focus">
      <q-btn
        round
        dense
        @click="addItemInCart(-1)"
        flat
        :icon="cartQty == 1 ? 'delete' : 'remove'"
        size="9px"
        type="number"
        :disable="disable"
      />
      <q-input
        ref="cartInput"
        @blur="onBlur"
        class="text-center qtyGroup"
        outlined
        mask="####"
        v-model="cartQty"
        dense
        @focus="focus = true"
        @keyup.enter="$event.target.blur()"
        unmasked-value
        :disable="disable"
      />
      <q-btn
        round
        dense
        @click="addItemInCart(+1)"
        flat
        icon="add"
        size="0.6rem"
        :class="cartQty >= qtyLimit ? 'disable' : ''"
        :disable="disable || cartQty >= qtyLimit"
      />
    </template>
    <q-btn
      v-else-if="showSelectButton"
      color="secondaryOnBody"
      @click="addItemInCart(+1)"
      outline
      label="Select"
      class="full-width"
    />
  </div>
  <div :class="['relative-position', 'product-card-actions']" v-else>
    <!-- CARD -->
    <div class="outstock-btn">
      <span>Out of Stock</span>
    </div>
    <q-btn
      flat
      round
      dense
      icon="notifications_none"
      class="notify-btn"
      :disable="loading"
      :loading="loading"
      @click.stop="setNotificationForProduct"
      @mousedown.prevent
    >
      <q-tooltip>Notify Me</q-tooltip>
    </q-btn>
  </div>
</template>

<script>
import { useOrderStore } from 'src/stores/order'
import { mapState } from 'pinia'
import { useProductStore } from 'src/stores/product'
const defaultQtyLimit = 9999

export default {
  name: 'OrderCartButton',
  props: {
    isinstock: {
      type: Boolean,
      default: true,
    },
    showSelectButton: {
      type: Boolean,
      default: true,
    },
    productId: {
      type: String,
      default: () => {
        return ''
      },
    },
    product: {
      type: Object,
      default: () => {
        return {}
      },
    },
    updateState: {
      type: Boolean,
      default: false,
    },
    updateUsingAPI: {
      type: Boolean,
      default: false,
    },
    orderId: {
      type: String,
      default: '',
    },
    showMaxErrorOnTop: {
      type: Boolean,
      default: true,
    },
    disable: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      quantity: null,
      showMaxError: false,
      showMaxErrorTimer: null,
      focus: false,
      timer: null,
      loading: false,
      orderStore: useOrderStore(),
      productStore: useProductStore(),
    }
  },
  computed: {
    ...mapState(useOrderStore, ['recentItems']),
    itemInfo() {
      return this.recentItems[this.productId] || this.product
    },
    qtyLimit() {
      if (!this.product) return defaultQtyLimit
      const { qtyOnHand, qtyLimit, inventoryLimit } = this.product
      if (qtyOnHand) {
        return Math.min(qtyLimit, qtyOnHand) || defaultQtyLimit
      }
      if (inventoryLimit) {
        return Math.min(qtyLimit, inventoryLimit) || defaultQtyLimit
      }
      return qtyLimit || defaultQtyLimit
    },
    cartQty: {
      get() {
        if (this.quantity !== null) {
          return this.quantity
        }
        let info = this.recentItems[this.productId] || this.product
        return parseInt(info.qty || 0)
      },
      set(val) {
        this.quantity = val || 0
      },
    },
    isProductAlreadyNotified() {
      return this.notifiedProductIds.has(this.productId)
    },
  },
  watch: {
    'itemInfo.qty': function (newVal, oldVal) {
      if (newVal != oldVal) {
        this.cartQty = newVal
      }
    },
  },
  methods: {
    onBlur() {
      this.focus = false
      if (this.cartQty != this.product.qty) {
        let cartQty = parseInt(this.cartQty) || 0
        let newCartQty = Math.max(cartQty, 0)
        this.cartQty = newCartQty || 1
        newCartQty > 0 ? this.updateCart() : this.deleteLineItem()
      }
    },
    checkMaxLimit() {
      if (this.cartQty >= this.qtyLimit) {
        this.showMaxError = true

        if (this.showMaxErrorTimer) {
          clearTimeout(this.showMaxErrorTimer)
          this.showMaxErrorTimer = null
        }

        this.showMaxErrorTimer = setTimeout(() => {
          this.showMaxError = false
        }, 2000)

        this.cartQty = this.qtyLimit
      }
    },
    addItemInCart(incrementQty) {
      let cartQty = parseInt(this.cartQty) || 0
      let increment = parseInt(incrementQty) || 0
      let timeout = cartQty > 1 ? 300 : 0

      // Ensure cartQty does not go below zero
      let newCartQty = Math.max(cartQty + increment, 0)
      this.cartQty = newCartQty || 1
      this.checkMaxLimit()

      if (this.timer) {
        clearTimeout(this.timer)
        this.timer = null
      }

      this.timer = setTimeout(() => {
        newCartQty > 0 ? this.updateCart() : this.deleteLineItem()
      }, timeout)
    },
    updateCart(quantity) {
      /* START: Show error when qty is more than available qty */
      let info = this.recentItems[this.productId] || this.product
      let currentQty = parseInt(info.qty || 0)
      let newCartQty = parseInt(quantity || 0) || this.cartQty

      let showMaxLimitError = false
      if (this.businessConfigurations.allowOutOfStockSell === false) {
        if (currentQty == this.qtyLimit && newCartQty > currentQty) {
          this.checkMaxLimit()
          if (this.product.inventoryLimit > this.product.qtyLimit) {
            return this.showError(
              `${this.product.qtyLimit} is the maximum purchase limit for an item, so you can't place an order with more than ${this.product.qtyLimit} Qty.`
            )
          } else {
            return this.showError(
              `Only ${this.qtyLimit} Qty left in stock so you cannot add more than ${this.qtyLimit} to your cart!`
            )
          }
        } else if (newCartQty > this.qtyLimit) {
          showMaxLimitError = true
        }
      }
      /* END: Show error when qty is more than available qty */
      this.checkMaxLimit()

      if (!quantity && quantity !== 0) {
        quantity = this.cartQty
      }
      let objItem = {
        [this.productId]: {
          ...this.itemInfo,
          qty: quantity,
        },
      }

      if (this.updateState) {
        if (!quantity) {
          this.orderStore.deleteRecentItem(this.productId)
        } else {
          if (
            this.showSelectButton &&
            objItem[this.productId].linkedProductID
          ) {
            this.orderStore
              .getLinkedProducts({
                productID: objItem[this.productId].productID,
                locationID: this.currentLocationId,
              })
              .then((response) => {
                if (response && response.data.data && response.data.success) {
                  let linkedProduct = Object.assign({}, response.data.data)
                  linkedProduct.qty = quantity
                  objItem[this.productId].linkedProduct = linkedProduct
                  this.orderStore.setRecentItems(objItem)
                } else {
                  this.orderStore.setRecentItems(objItem)
                }
              })
          } else {
            this.orderStore.setRecentItems(objItem)
          }
        }
      }

      if (this.updateUsingAPI && this.orderId) {
        let productLineItem = {
          productID: this.productId,
          qty: this.cartQty,
          instruction: this.product.instruction,
        }
        if (this.product.isSubscribeForAutoShip) {
          productLineItem = {
            ...productLineItem,
            frequency: this.product.frequency || 0,
            orderLineItemOrderType: this.product.orderLineItemOrderType || 0,
            subscriptionID: this.product.subscriptionID || undefined,
          }
        }
        let params = {
          orderID: this.orderId,
          editOrderLineItems: [productLineItem],
        }

        this.orderStore
          .updateOrder(params)
          .then((response) => {
            /* START: Show error when qty is more than available qty */
            if (response.success && showMaxLimitError) {
              if (this.product.inventoryLimit > this.product.qtyLimit) {
                return this.showError(
                  `${this.product.qtyLimit} is the maximum purchase limit for an item, so you can't order with more than ${this.product.qtyLimit} Qty.`
                )
              } else {
                return this.showError(
                  `Only ${this.qtyLimit} Qty left in stock so you cannot add more than ${this.qtyLimit} to your order!`
                )
              }
            }
            /* END: Show error when qty is more than available qty */
          })
          .catch((error) => {
            let info = this.recentItems[this.productId] || this.product
            this.cartQty = parseInt(info.qty || 0)
            this.ftxConfirmCoupon(
              '',
              error.toString(),
              false,
              'Ok',
              true,
              false,
              'info-filled',
              '3em',
              true,
              false,
              false,
              true
            )
          })
      }
    },
    deleteLineItem() {
      if (this.updateState) {
        this.orderStore.deleteRecentItem(this.productId)
      }
      if (this.updateUsingAPI && this.orderId) {
        let productLineItem = {
          productID: this.productId,
          isDeleted: true,
          instruction: this.product.instruction,
        }
        if (this.product.isSubscribeForAutoShip) {
          productLineItem = {
            ...productLineItem,
            frequency: this.product.frequency || 0,
            orderLineItemOrderType: this.product.orderLineItemOrderType || 0,
            subscriptionID: this.product.subscriptionID || undefined,
          }
        }

        let params = {
          orderID: this.orderId,
          editOrderLineItems: [productLineItem],
        }

        this.orderStore.updateOrder(params)
      }
    },
    setNotificationForProduct() {
      if (!this.productId) return

      if (this.isLoggedIn && this.customer.emailID)
        return this.notifyLoggedInUser()
      else return this.notifyUserWithoutEmail()
    },
    async notifyLoggedInUser() {
      this.loading = true
      let body = {
        productID: this.productId,
        locationID: this.currentLocationId,
        emailID: this.customer.emailID,
        customerID: this.customer?.customerID,
      }
      let response = await this.productStore.setProductNotification(body)
      this.loading = false

      if (response && response.success) {
        return this.showSuccess(
          `You will be notified through your registered email when "${this.product?.title}" is back in stock!`
        )
      } else if (response?.errors && response?.errors?.length > 0) {
        return this.showError(
          response?.errors[0]?.descreption || 'Something went wrong.'
        )
      }
    },
    async notifyUserWithoutEmail() {
      this.changeDialogState({
        dialog: 'NotifyDialog',
        val: true,
        properties: {
          productId: this.productId,
          productName: this.product?.title,
          isOnlyEmail: this.isLoggedIn ?? false,
          parentDialog: 'AddOrderItemDialog',
        },
      })
    },
  },
}
</script>
<style lang="scss">
.quantityBox {
  .error {
    left: 0;
    right: 0;
  }
}
</style>
